import { z } from "zod";

const revision = z.string().regex(/^[1-9]\d*$/u);
const watermark = z.string().regex(/^\d+$/u);
const timestamp = z.string().datetime({ offset: true });
export const merchantHomeIntentTypeSchema = z.enum([
  "bind_store", "open_store", "product_discovery", "create_strategy",
  "adjust_approval", "explore_opportunity", "configure_tracking",
]);
export const merchantHomeActionPurposeSchema = z.enum([
  "discuss_opportunity", "plan_sample_test", "start_storyboard", "plan_monthly_opportunity", "continue_store_opening",
  "plan_pricing_recalculation", "plan_product_selection", "plan_creator_evaluation", "review_content_strategy", "review_policy_compliance",
  "plan_product_price_update", "plan_ad_campaign_adjustment", "plan_inventory_adjustment", "plan_fulfillment_shipment",
  "plan_aftersales_decision", "plan_social_publish_or_reply", "plan_email_send_or_reply",
]);
export const merchantHomeBindingStateSchema = z.enum([
  "unbound", "bound", "requires_selection", "sync_error", "reauthorization_required",
]);
export const merchantHomeWorkKindSchema = z.enum(["completed", "running", "attention", "approval"]);
export const merchantHomeScopeSchema = z.object({
  organization_id: z.string().min(1), shop_binding_id: z.string(),
  shop_display_name: z.string(), market: z.string(), category: z.string(),
}).strict();
export const merchantHomeWorkItemSchema = z.object({
  item_id: z.string().min(1), canonical_work_key: z.string().min(1),
  kind: merchantHomeWorkKindSchema,
  status: z.enum(["succeeded", "running", "approval_required", "waiting_input", "blocked", "failed", "expired"]),
  organization_id: z.string().min(1), shop_binding_id: z.string(),
  visibility: z.enum(["creator_private", "domain_shared"]),
  safe_title: z.string(), safe_summary: z.string(),
  started_at: timestamp.nullable(), completed_at: timestamp.nullable(), updated_at: timestamp,
  result_revision: revision, read_revision: watermark,
  source: z.object({
    kind: z.enum(["execution", "scheduled_run", "async_run"]), available: z.boolean(),
    stale: z.boolean(), conversation_id: z.string().min(1).optional(), source_id: z.string().min(1),
  }).strict(),
  actions: z.object({
    can_open_source: z.boolean(), can_act: z.boolean(), disabled_reason: z.string(),
    plan_hash: z.string(), state_revision: revision, resource_ids: z.array(z.string()),
  }).strict(),
}).strict();
export const merchantHomeOverviewSchema = z.object({
  schema_version: z.literal(1), resolved_scope: merchantHomeScopeSchema,
  binding_state: merchantHomeBindingStateSchema,
  block_states: z.record(z.string(), z.object({
    state: z.enum(["ready", "partial", "unavailable", "requires_selection", "forbidden"]),
    error_code: z.string(),
  }).strict()),
  strategy: z.object({
    state: z.enum(["ready", "empty", "requires_selection", "unavailable", "forbidden"]),
    summary: z.string(), scope_revision: watermark,
    dimensions: z.array(z.object({ key: z.string(), headline: z.string(), strategy_id: z.string(), version_id: z.string() }).strict()),
  }).strict(),
  counts: z.object({
    completed: z.number().int().nonnegative(), running: z.number().int().nonnegative(),
    attention: z.number().int().nonnegative(), approval: z.number().int().nonnegative(),
  }).strict().nullable(),
  snapshot_id: z.string(), as_of: timestamp,
  window: z.object({ start: timestamp, end: timestamp }).strict(),
  resolved_timezone: z.string().min(1), updated_at: timestamp,
}).strict();
export const merchantHomeWorkPageSchema = z.object({
  items: z.array(merchantHomeWorkItemSchema), total: z.number().int().nonnegative(),
  next_cursor: z.string(), has_more: z.boolean(), snapshot_id: z.string().min(1),
}).strict();
export const merchantHomeWorkDetailSchema = z.object({ item: merchantHomeWorkItemSchema }).strict();
export const merchantHomeReadResponseSchema = z.object({ item_id: z.string(), read_revision: revision }).strict();
export const merchantHomeIntentResponseSchema = z.object({
  operation_id: z.string(), conversation_id: z.string(), message_id: z.string(), opening_request: z.string(),
  send_status: z.enum(["pending", "sent", "failed", "uncertain"]), replay: z.boolean(),
  action_purpose: merchantHomeActionPurposeSchema.optional(),
}).strict();
export const merchantHomeIntentStatusSchema = merchantHomeIntentResponseSchema.extend({ can_resume: z.boolean() });
export const merchantHomeApprovalResponseSchema = z.object({
  operation_id: z.string(), execution_id: z.string(), status: z.enum(["pending", "accepted", "retryable_failed"]),
}).strict();
export const merchantHomeLegacyCardTypes = [
  "daily_product", "advertising_content", "auto_listing", "promotion_calendar",
  "media_reference", "competitor_updates", "creator_tracking", "market_trends",
] as const;
// 首页左侧仅消费 CWM 已发布机会；legacy 8 类仍只描述旧 `/opportunities` wire。
export const merchantHomeCwmOpportunityTypes = [
  "daily_product", "advertising_content", "market_trends",
  "monthly_opportunity", "media_reference",
] as const;
export const merchantHomeCapabilityCardTypes = [
  "monthly_opportunity", "store_opening", "pricing_calculator", "product_selection_analysis",
  "creator_recommendation", "report_export", "content_strategy", "policy_compliance",
] as const;
export const merchantHomeStoreManagementCardTypes = [
  "store_operations", "product_performance", "product_diagnosis", "advertising_management",
  "advertising_performance", "inventory_management", "fulfillment_logistics", "after_sales_reviews",
  "profit_settlement", "social_media", "email_management",
] as const;

export const merchantHomeOpportunityTypeSchema = z.enum(merchantHomeLegacyCardTypes);
// `/opportunities` 的 legacy 枚举保持上面的 8 类；只有 `/card-content` 与个人布局使用扩展枚举。
export const merchantHomeWorkbenchCardTypeSchema = z.enum([
  ...merchantHomeLegacyCardTypes,
  ...merchantHomeCapabilityCardTypes,
  ...merchantHomeStoreManagementCardTypes,
]);
const messagePayload = z.object({ summary: z.string(), error_code: z.string() }).strict();
const contentPayload = z.object({
  summary: z.string(), market: z.string(), category: z.string(), facts: z.array(z.string()),
  suggestions: z.array(z.string()), estimate: z.string(), stale: z.boolean(),
}).strict();
const mediaPayload = z.object({
  summary: z.string(), media_type: z.string(), public_url: z.string(), viewer_url: z.string(), relation: z.string(),
}).strict();
const trackingPayload = z.object({
  summary: z.string(), object_count: z.number().int().nonnegative(), update_count: z.number().int().nonnegative().nullable(),
  objects: z.array(z.object({ id: z.string(), label: z.string(), state: z.string() }).strict()), sync_state: z.string(),
}).strict();
const listingPayload = z.object({
  summary: z.string(), draft_id: z.string(), provider: z.string(), status: z.string(), requires_confirmation: z.boolean(),
}).strict();
export const merchantHomeOpportunitySchema = z.object({
  id: z.string().min(1), type: merchantHomeOpportunityTypeSchema,
  scope: z.object({
    organization_id: z.string(), shop_binding_id: z.string(), market: z.string(), category: z.string(), binding_state: merchantHomeBindingStateSchema,
  }).strict(),
  state: z.enum(["ready", "requires_binding", "requires_setup", "empty", "unavailable", "error"]),
  title: z.string(), payload: z.union([messagePayload, contentPayload, mediaPayload, trackingPayload, listingPayload]),
  source_refs: z.array(z.object({ kind: z.string(), id: z.string(), label: z.string(), url: z.string(), as_of: z.string() }).strict()),
  published_at: timestamp.nullable(), expires_at: timestamp.nullable(), updated_at: z.union([timestamp, z.literal("")]),
  actions: z.array(z.object({
    intent_type: merchantHomeIntentTypeSchema, label: z.string(), reference: z.string(), enabled: z.boolean(), disabled_reason: z.string(),
  }).strict()),
}).strict().superRefine((card, context) => {
  const schema = card.state !== "ready" ? messagePayload
    : card.type === "media_reference" ? mediaPayload
      : card.type === "auto_listing" ? listingPayload
        : ["competitor_updates", "creator_tracking"].includes(card.type) ? trackingPayload : contentPayload;
  if (!schema.safeParse(card.payload).success) context.addIssue({ code: "custom", message: "商机类型、状态与内容不匹配", path: ["payload"] });
});
export const merchantHomeOpportunitiesSchema = z.object({
  items: z.array(merchantHomeOpportunitySchema), total: z.number().int().nonnegative(), next_cursor: z.string(), has_more: z.boolean(),
}).strict();
export type MerchantHomeOverview = z.infer<typeof merchantHomeOverviewSchema>;
export type MerchantHomeWorkItem = z.infer<typeof merchantHomeWorkItemSchema>;
export type MerchantHomeWorkKind = z.infer<typeof merchantHomeWorkKindSchema>;
export type MerchantHomeOpportunity = z.infer<typeof merchantHomeOpportunitySchema>;
export type MerchantHomeIntentType = z.infer<typeof merchantHomeIntentTypeSchema>;
export type MerchantHomeActionPurpose = z.infer<typeof merchantHomeActionPurposeSchema>;
export type MerchantHomeWorkbenchCardType = z.infer<typeof merchantHomeWorkbenchCardTypeSchema>;
